import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DatabaseZap, Loader2, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/context/AuthContext";

const SETS = [
  { key: "grir", label: "GR/IR (GL line items)" },
  { key: "invoices", label: "MIRO (FI line items, ref. type RMRP)" },
  { key: "migo", label: "MB51 (mvt 101/102 with PO)" },
];
const PROG = [["grir", "GR/IR"], ["invoices", "MIRO"], ["migo_headers", "MB51 headers"], ["migo", "MB51 items"], ["migo_values", "MB51 values"]];

const ProgressLine = ({ job, secs }) => (
  <p className="basis-full text-xs text-sky-900 flex flex-wrap gap-x-4 gap-y-1" data-testid="preco-sap-progress">
    <span className="font-semibold">{secs}s</span>
    {PROG.filter(([k]) => job.progress?.[k] !== undefined).map(([k, l]) => (
      <span key={k} data-testid={`preco-sap-progress-${k}`}>{l} — {job.progress[k].toLocaleString()} rows{job.stage?.[k] === "done" ? " ✓" : "…"}</span>
    ))}
    {!Object.keys(job.progress || {}).length && <span>Connecting to SAP…</span>}
  </p>
);

// Pulls the three SAP inputs straight from SAP for the From–To dates and stores them like an upload (replaces that period).
export function SapExtractPanel({ workspace, dates, onDone }) {
  const [pick, setPick] = useState({ grir: true, invoices: true, migo: true });
  const [gl, setGl] = useState("21120000");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [secs, setSecs] = useState(0);
  const t0 = useRef(0);

  const finish = (r) => {
    setBusy(false); setJob(null);
    if (r.status === "cancelled") { toast.info(r.error); return; }
    if (r.status === "error") { toast.error(r.error); return; }
    onDone({ coverage: r.result.coverage, runs: r.result.runs });
    toast.success(r.result.added.map((a) => `${a.label}: ${a.rows_added.toLocaleString()} rows`).join(" · "));
  };

  const poll = async (id, misses = 0) => {
    setSecs(Math.round((Date.now() - t0.current) / 1000));
    try {
      const r = (await axios.get(`${API}/purchase-reco/job/${id}`)).data;
      if (r.status === "running") { setJob({ id, ...r }); setTimeout(() => poll(id), 2000); return; }
      finish(r);
    } catch (e) {
      if (e.response?.status === 404 || misses >= 5) { setBusy(false); setJob(null); toast.error(e.response?.data?.detail || "Lost track of the fetch — run it again"); return; }
      setTimeout(() => poll(id, misses + 1), 3000);
    }
  };

  const run = async () => {
    if (!workspace.trim()) { toast.error("Enter the entity name first"); return; }
    if (!dates.from || !dates.to) { toast.error("Pick the From and To dates to extract"); return; }
    const datasets = Object.keys(pick).filter((k) => pick[k]);
    if (!datasets.length) { toast.error("Tick at least one dataset"); return; }
    setBusy(true); setSecs(0); t0.current = Date.now();
    try {
      const { job: id } = (await axios.post(`${API}/purchase-reco/sap-extract`, { workspace: workspace.trim(), date_from: dates.from, date_to: dates.to, datasets, grir_gl: gl })).data;
      setJob({ id, progress: {} });
      setTimeout(() => poll(id), 1500);
    } catch (e) { setBusy(false); toast.error(e.response?.data?.detail || "SAP extraction failed"); }
  };

  const stop = async () => {
    if (!job?.id) return;
    try { await axios.post(`${API}/purchase-reco/job/${job.id}/cancel`); toast.info("Stopping the fetch…"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not stop"); }
  };

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="preco-sap-panel">
      <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><DatabaseZap className="h-4 w-4 text-sky-700" />Fetch from SAP for the From–To dates</p>
      {SETS.map((s) => (
        <label key={s.key} className="flex items-center gap-1.5 text-xs text-slate-700">
          <Checkbox checked={pick[s.key]} onCheckedChange={(v) => setPick({ ...pick, [s.key]: !!v })} data-testid={`preco-sap-${s.key}`} />{s.label}
        </label>
      ))}
      <label className="flex items-center gap-1.5 text-xs text-slate-600">GR/IR GL
        <Input value={gl} onChange={(e) => setGl(e.target.value)} className="h-8 w-28 text-xs bg-white" data-testid="preco-sap-gl" />
      </label>
      <Button size="sm" onClick={run} disabled={busy} className="bg-sky-700 hover:bg-sky-800 ml-auto" data-testid="preco-sap-fetch-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DatabaseZap className="h-4 w-4 mr-1" />}{busy ? "Fetching from SAP…" : "Fetch & store"}
      </Button>
      {busy && job?.id && (
        <Button size="sm" onClick={stop} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="preco-sap-stop-btn">
          <Square className="h-3.5 w-3.5 mr-1" />Stop fetch
        </Button>
      )}
      {busy && job && <ProgressLine job={job} secs={secs} />}
      <p className="basis-full text-[11px] text-slate-500">Replaces whatever is stored for those dates (uploads or earlier fetches); other periods stay. The Tally register is still uploaded.</p>
    </div>
  );
}
